/**
 * Fila do PDF no site.
 *
 * O servidor desenha o arquivo em segundo plano (`apps/api/src/lib/fila-pdf.ts`).
 * Do lado de cá, o que o cliente precisa é simples e teimoso:
 *
 *  - clicar em "Baixar PDF" **não trava a tela**: o pedido entra na fila e a
 *    resposta volta na hora;
 *  - enquanto o arquivo é desenhado, a página continua utilizável — dá para
 *    mexer nos passos, abrir outro miolo ou fechar a aba, que o desenho segue;
 *  - quando o arquivo fica pronto, o download **começa sozinho** — mas só na aba
 *    que pediu. Voltar à página depois mostra "Baixar PDF", e o clique é
 *    instantâneo porque o servidor reaproveita o arquivo guardado.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

export type EstadoDaFila = 'PARADO' | 'GERANDO' | 'PRONTO' | 'FALHOU';

/** De quanto em quanto tempo perguntar o estado (o desenho leva de 10 s a alguns minutos). */
const INTERVALO_MS = 3000;

/**
 * Depois disso o site para de perguntar e explica.
 *
 * Não é desistir do arquivo: ele continua sendo desenhado no servidor. É não
 * ficar perguntando para sempre numa aba esquecida.
 */
const LIMITE_MS = 15 * 60 * 1000;

export interface FilaDoPdf {
  estado: EstadoDaFila;
  /** true enquanto o arquivo é desenhado — é o que o botão mostra. */
  ocupado: boolean;
  /** O que dizer ao usuário: falha, arquivo pronto ou fila em andamento. */
  aviso: string | null;
  /** Pede o PDF. Volta na hora; o download sai quando o arquivo ficar pronto. */
  pedir: () => Promise<void>;
}

const esperar = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useFilaDoPdf(opcoes: { mioloId: string | null; nome: string }): FilaDoPdf {
  const { mioloId, nome } = opcoes;
  const [estado, setEstado] = useState<EstadoDaFila>('PARADO');
  const [aviso, setAviso] = useState<string | null>(null);
  const vivo = useRef(true);
  /** Evita dois acompanhamentos ao mesmo tempo (dois cliques, re-render). */
  const acompanhando = useRef(false);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  /**
   * Ao abrir a página, pergunta se já existe geração em andamento.
   *
   * Acontece quando a pessoa pediu o PDF e saiu da página (ou fechou a aba): ao
   * voltar, em vez de "Baixar PDF" ela vê que o arquivo está sendo desenhado.
   * Aqui **não** se baixa sozinho: o download automático é de quem clicou.
   */
  useEffect(() => {
    if (!mioloId) return;
    let ativo = true;

    void api
      .estadoDoPdf(mioloId)
      .then((atual) => {
        if (!ativo || !vivo.current) return;
        if (atual.estado === 'GERANDO') {
          setEstado('GERANDO');
          setAviso('O PDF está sendo desenhado no servidor. Pode continuar navegando.');
        }
        if (atual.estado === 'FALHOU') {
          setEstado('FALHOU');
          setAviso(atual.erro);
        }
      })
      .catch(() => undefined);

    return () => {
      ativo = false;
    };
  }, [mioloId]);

  const pedir = useCallback(async () => {
    if (!mioloId || acompanhando.current) return;

    acompanhando.current = true;
    setAviso(null);
    setEstado('GERANDO');

    const começou = Date.now();

    try {
      const resposta = await api.gerarPdf(mioloId);

      if (resposta.estado === 'PRONTO') {
        await api.baixarPdf(mioloId, nome);
        if (vivo.current) setEstado('PRONTO');
        return;
      }

      /* Entrou na fila: pergunta o estado até ficar pronto. */
      for (;;) {
        if (!vivo.current) return;

        if (Date.now() - começou > LIMITE_MS) {
          setAviso(
            'A geração está demorando mais que o normal. O arquivo continua sendo desenhado — daqui a pouco ele aparece pronto nesta página.',
          );
          return;
        }

        await esperar(INTERVALO_MS);
        if (!vivo.current) return;

        const atual = await api.estadoDoPdf(mioloId);

        if (atual.estado === 'PRONTO') {
          await api.baixarPdf(mioloId, nome);
          if (vivo.current) {
            setEstado('PRONTO');
            setAviso('PDF pronto: o download começou.');
          }
          return;
        }

        if (atual.estado === 'FALHOU') {
          setEstado('FALHOU');
          setAviso(atual.erro ?? 'Não foi possível gerar o PDF agora. Tente de novo.');
          return;
        }
      }
    } catch (falha) {
      if (!vivo.current) return;
      setEstado('FALHOU');
      setAviso(falha instanceof Error ? falha.message : 'Não foi possível gerar o PDF.');
    } finally {
      acompanhando.current = false;
    }
  }, [mioloId, nome]);

  return { estado, ocupado: estado === 'GERANDO', aviso, pedir };
}
